import React, { useEffect, useState, useRef } from "react";
import { ArrowLeft, Loader, Send } from "lucide-react";
import {Link, useParams, useSearchParams} from "react-router-dom";
import Button from "../UI/Button";
import useApi from "../../utils/useApi";
import {useNavVisibility} from "../NavVisibilityContext/NavVisibilityContext";
import LoadingScreen from "../LoadingScreen/LoadingScreen";

const MessageShow = ({ user, handleShowHeader, handleShowSettingsHeader }) => {
    const { id } = useParams();
    const { fetchThread, sendMessage } = useApi();
    const { setIsNavVisible } = useNavVisibility();
    const [searchParams, setSearchParams] = useSearchParams();
    const [thread, setThread] = useState(null);
    const [client, setClient] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [message, setMessage] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMoreMessages, setHasMoreMessages] = useState(true);
    const messagesContainerRef = useRef(null);
    const textareaRef = useRef(null);
    const [threadId, setThreadId] = useState(parseInt(id));
    const [messages, setMessages] = useState([]);
    const [shouldScrollToBottom, setShouldScrollToBottom] = useState(true);
    const windowWidth = window.innerWidth;

    useEffect(() => {
        initializeMessages();
        handleShowHeader(false);
        handleShowSettingsHeader(false);
        setIsNavVisible(false);
    }, []);

    useEffect(() => {
        if (shouldScrollToBottom && !isLoading && messagesContainerRef.current) {
            scrollToBottom();
            setShouldScrollToBottom(false);
        }
    }, [isLoading, messages, shouldScrollToBottom]);

    // Auto-resize textarea
    useEffect(() => {
        const textarea = textareaRef.current;
        if (textarea) {
            // Reset height to auto to get the correct scrollHeight
            textarea.style.height = 'auto';
            // Set new height based on scrollHeight
            const newHeight = Math.min(textarea.scrollHeight, 200); // Max height of 200px
            textarea.style.height = `${newHeight}px`;
        }
    }, [message]);

    const scrollToBottom = () => {
        if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
    };

    const initializeMessages = async () => {
        let response;

        response = await fetchThread(id, 1);

        const transformedThread = {
            id: response.thread.id,
            subject: response.thread.subject,
            string: response.who,
        };

        setClient(response.customer);
        setThread(transformedThread);
        // Reverse the messages array before setting it
        setMessages([...response.thread.messages.data].reverse());
        setHasMoreMessages(response.pagination.has_more_pages);
        setIsLoading(false);
        setShouldScrollToBottom(true);
    };



    const handleScroll = async (e) => {
        const { scrollTop } = e.target;
        if (scrollTop < 100 && !isLoadingMore && hasMoreMessages) {
            await loadMoreMessages();
        }
    };

    const loadMoreMessages = async () => {
        try {
            setIsLoadingMore(true);
            const nextPage = currentPage + 1;
            const container = messagesContainerRef.current;
            const distanceFromBottom = container.scrollHeight - container.scrollTop;

            const response = await fetchThread(id, nextPage);

            // Add new messages to the beginning of the array
            setMessages(prevMessages => [...response.thread.messages.data.reverse(), ...prevMessages]);
            setCurrentPage(nextPage);
            setHasMoreMessages(response.pagination.has_more_pages);

            requestAnimationFrame(() => {
                if (container) {
                    container.scrollTop = container.scrollHeight - distanceFromBottom;
                }
            });
        } catch (error) {
            console.error('Error loading more messages:', error);
        } finally {
            setIsLoadingMore(false);
        }
    };

    const formatMessageTime = (date) => {
        const now = new Date();
        const messageDate = new Date(date);

        // Check if the message is from today
        const isToday = messageDate.toDateString() === now.toDateString();

        // Format hours and minutes
        let hours = messageDate.getHours();
        const minutes = messageDate.getMinutes();
        const ampm = hours >= 12 ? 'pm' : 'am';

        // Convert to 12-hour format
        hours = hours % 12;
        hours = hours ? hours : 12; // Convert 0 to 12

        // Create the formatted time string
        const timeString = `${hours}:${minutes.toString().padStart(2, '0')}${ampm}`;

        return isToday ? `Today at ${timeString}` : messageDate.toLocaleDateString();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        console.log(thread);
        let threadIdToUpdate = threadId;


        if(!threadIdToUpdate) {
            threadIdToUpdate = thread.id;
        }

        if (message.trim()) {
            try {
                // Send the message to the API
                console.log('Sending message');
                const response = await sendMessage(threadIdToUpdate, message);

                // Create a new message object
                const newMessage = {
                    id: response.id || Date.now(), // Use API response id if available, fallback to timestamp
                    body: message,
                    created_at: formatMessageTime(new Date()),
                    user: {
                        id: user?.id // Use the current user's ID
                    }
                };

                // Update the messages array
                setMessages(prevMessages => [...prevMessages, newMessage]);

                // Clear the input and trigger scroll
                setMessage('');
                setShouldScrollToBottom(true);

                // Reset textarea height
                if (textareaRef.current) {
                    textareaRef.current.style.height = 'auto';
                }
            } catch (error) {
                console.error('Error sending message:', error);
                // You might want to add error handling UI here
            }
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-screen w-full fixed inset-0 bg-white dark:bg-gray-900">
                <LoadingScreen />
            </div>
        );
    }

    const getParticipantName = () => {
        if (!thread) return '';
        return thread.string || 'Unknown Participant';
    };

    return (
        <div className="fixed inset-0 flex flex-col bg-white dark:bg-gray-900 z-10 page-container">
            <div className="message-container flex flex-col inset-0 fixed">
                <div className="border-b border-gray-200 dark:border-gray-700 p-4 flex items-center bg-white dark:bg-gray-800">
                    <Link to="/messages">
                        <Button variant="ghost" className="mr-4">
                            <ArrowLeft className="w-5 h-5"/>
                        </Button>
                    </Link>
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {user.user_type === 'admin' ? (
                                <Link to={`/client-progress/${client.id}`}>Chat with {getParticipantName()}</Link>
                            ): (
                                <span>Chat with {getParticipantName()}</span>
                            )}
                        </h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            {thread?.subject}
                        </p>
                    </div>
                </div>

                <div
                    ref={messagesContainerRef}
                    onScroll={handleScroll}
                    className="flex-1 overflow-y-auto p-4 space-y-4"
                >
                    {isLoadingMore && (
                        <div className="flex justify-center p-2">
                            <Loader className="w-4 h-4 animate-spin text-gray-500 dark:text-gray-400"/>
                        </div>
                    )}

                    {messages.map((msg) => (
                        <div
                            key={msg.id}
                            className={`flex ${msg.user.id === user?.id ? 'justify-end' : 'justify-start'}`}
                        >
                            <div
                                className={`max-w-[70%] rounded-lg p-3 ${
                                    msg.user.id === user?.id
                                    ? 'bg-blue-500 text-white'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                                }`}
                            >
                                <p className="text-sm break-words">{msg.body}</p>
                                <span className="text-xs opacity-70 mt-1 block">
                                {msg.created_at}
                            </span>
                            </div>
                        </div>
                    ))}
                </div>

                <form onSubmit={handleSubmit} className="border-t border-gray-200 dark:border-gray-700 p-4 bg-white dark:bg-gray-800">
                    <div className="flex space-x-2">
                    <textarea
                        ref={textareaRef}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Type your message..."
                        className="flex-1 min-h-[40px] max-h-[200px] overflow-y-auto rounded-lg border border-gray-300 dark:border-gray-600
                               bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-2
                               focus:outline-none focus:ring-2 focus:ring-blue-500
                               dark:focus:ring-blue-600 focus:border-transparent"
                        style={{ resize: 'none' }}
                    />
                        <Button type="submit" disabled={!message.trim()}>
                            <Send className="w-4 h-4"/>
                        </Button>
                    </div>
                </form>
            </div>

        </div>
    );
};

export default MessageShow;
